import type { VirtualKeyboardLayout } from "mathlive"

const MATH_CORE_LAYOUT: VirtualKeyboardLayout = {
  id: "math-core",
  label: "Core",
  labelClass: "MLK__tex-math",
  tooltip: "Core math symbols",
  rows: [
    [
      { latex: "\\frac{#?}{#?}", class: "small" },
      { latex: "\\sqrt{#?}", class: "small" },
      { latex: "^{#?}", class: "small" },
      { latex: "_{#?}", class: "small" },
      { latex: "\\left(#?\\right)", class: "small" },
      { latex: "\\left|#?\\right|", class: "small" },
      { latex: "\\pm" },
      { latex: "\\mp" },
      { label: "[backspace]", class: "action hide-shift" },
    ],
    [
      "=",
      { latex: "\\ne" },
      "<",
      ">",
      { latex: "\\le" },
      { latex: "\\ge" },
      { latex: "\\approx" },
      { latex: "\\equiv" },
      { latex: "\\infty" },
    ],
    [
      "x",
      "y",
      "z",
      "n",
      { latex: "\\pi" },
      { latex: "\\theta" },
      { latex: "\\alpha" },
      { latex: "\\beta" },
      { latex: "\\gamma" },
    ],
    [
      "+",
      "-",
      { latex: "\\times" },
      { latex: "\\div" },
      { latex: "\\cdot" },
      { latex: "\\%", insert: "\\%" },
      "[left]",
      "[right]",
    ],
  ],
}

const MATH_CALCULUS_LAYOUT: VirtualKeyboardLayout = {
  id: "math-calculus",
  label: "Calc",
  labelClass: "MLK__tex-math",
  tooltip: "Calculus and functions",
  rows: [
    [
      { latex: "\\sum_{#?}^{#?}", class: "small" },
      { latex: "\\prod_{#?}^{#?}", class: "small" },
      { latex: "\\int", insert: "\\int " },
      { latex: "\\int_{#?}^{#?}", class: "small" },
      { latex: "\\lim_{#?\\to#?}", class: "small" },
      { latex: "\\frac{d}{dx}", class: "small" },
      { latex: "\\frac{d^2}{dx^2}", class: "small" },
      { latex: "\\frac{\\partial}{\\partial x}", class: "small" },
    ],
    [
      { latex: "\\sin" },
      { latex: "\\cos" },
      { latex: "\\tan" },
      { latex: "\\log" },
      { latex: "\\ln" },
      { latex: "e^{#?}", class: "small" },
      { latex: "\\nabla" },
      { latex: "\\Delta" },
    ],
    [
      { latex: "\\vec{#?}", class: "small" },
      { latex: "\\hat{#?}", class: "small" },
      { latex: "\\bar{#?}", class: "small" },
      { latex: "\\overline{#?}", class: "small" },
      { latex: "\\begin{bmatrix}#?&#?\\\\#?&#?\\end{bmatrix}", class: "small" },
      { latex: "\\left\\{#?\\right\\}", class: "small" },
    ],
    ["[left]", "[right]", { label: "[backspace]", class: "action hide-shift" }],
  ],
}

const PHYSICS_MECHANICS_LAYOUT: VirtualKeyboardLayout = {
  id: "physics-mechanics",
  label: "Mech",
  labelClass: "MLK__tex-math",
  tooltip: "Mechanics",
  rows: [
    [
      { latex: "\\vec{F}" },
      { latex: "\\vec{v}" },
      { latex: "\\vec{a}" },
      { latex: "\\Delta x" },
      { latex: "\\Delta t" },
      { latex: "\\theta" },
      { latex: "\\mu" },
      "g",
      { label: "[backspace]", class: "action hide-shift" },
    ],
    [
      { latex: "F=ma", insert: "F=ma", class: "wide" },
      { latex: "p=mv", insert: "p=mv", class: "wide" },
      { latex: "W=Fd", insert: "W=Fd", class: "wide" },
      { latex: "P=\\frac{W}{t}", class: "wide small" },
      { latex: "E_k=\\frac{1}{2}mv^2", class: "xwide small" },
      { latex: "E_p=mgh", insert: "E_p=mgh", class: "wide" },
    ],
    [
      { latex: "v=u+at", insert: "v=u+at", class: "wide" },
      { latex: "s=ut+\\frac{1}{2}at^2", class: "xwide small" },
      { latex: "v^2=u^2+2as", insert: "v^2=u^2+2as", class: "xwide" },
      { latex: "\\omega" },
      { latex: "\\alpha" },
      { latex: "\\tau" },
      "I",
    ],
    ["[left]", "[right]"],
  ],
}

const PHYSICS_FIELDS_LAYOUT: VirtualKeyboardLayout = {
  id: "physics-fields",
  label: "E&M",
  labelClass: "MLK__tex-math",
  tooltip: "Electricity, waves, and fields",
  rows: [
    [
      "q",
      "V",
      "I",
      "R",
      { latex: "\\Omega" },
      { latex: "\\lambda" },
      "f",
      "T",
      { label: "[backspace]", class: "action hide-shift" },
    ],
    [
      { latex: "V=IR", insert: "V=IR", class: "wide" },
      { latex: "P=VI", insert: "P=VI", class: "wide" },
      { latex: "E=hf", insert: "E=hf", class: "wide" },
      { latex: "c=f\\lambda", insert: "c=f\\lambda", class: "wide" },
      { latex: "E=mc^2", insert: "E=mc^2", class: "wide" },
    ],
    [
      { latex: "F=k\\frac{q_1q_2}{r^2}", class: "xwide small" },
      { latex: "E=\\frac{F}{q}", class: "wide small" },
      { latex: "V=\\frac{W}{q}", class: "wide small" },
      { latex: "\\Phi=BA\\cos\\theta", class: "xwide small" },
    ],
    ["[left]", "[right]"],
  ],
}

const PHYSICS_UNITS_LAYOUT: VirtualKeyboardLayout = {
  id: "physics-units",
  label: "Units",
  labelClass: "MLK__tex-math",
  tooltip: "Physics units",
  rows: [
    [
      { latex: "\\mathrm{m}" },
      { latex: "\\mathrm{s}" },
      { latex: "\\mathrm{kg}" },
      { latex: "\\mathrm{N}" },
      { latex: "\\mathrm{J}" },
      { latex: "\\mathrm{W}" },
      { latex: "\\mathrm{Pa}" },
      { latex: "\\mathrm{Hz}" },
    ],
    [
      { latex: "\\mathrm{V}" },
      { latex: "\\mathrm{A}" },
      { latex: "\\Omega" },
      { latex: "\\mathrm{C}" },
      { latex: "\\mathrm{T}" },
      { latex: "\\mathrm{K}" },
      { latex: "^\\circ", insert: "^\\circ" },
    ],
    [
      { latex: "\\mathrm{m/s}" },
      { latex: "\\mathrm{m/s^2}" },
      { latex: "\\mathrm{N\\,m}" },
      { latex: "\\mathrm{J/kg}" },
      { latex: "\\mathrm{kg\\,m/s}" },
    ],
    ["[left]", "[right]", { label: "[backspace]", class: "action hide-shift" }],
  ],
}

const CHEMISTRY_FORMULA_LAYOUT: VirtualKeyboardLayout = {
  id: "chemistry-formulas",
  label: "Atom",
  labelClass: "MLK__tex-math",
  tooltip: "Chemistry formulas",
  rows: [
    [
      { latex: "+", insert: " + " },
      { latex: "-", insert: "-" },
      { latex: "->", insert: " -> " },
      { latex: "<=>", insert: " <=> " },
      { latex: "<-", insert: " <- " },
      { latex: "\\Delta", insert: "[Delta]" },
      "(",
      ")",
      "[separator-5]",
      { label: "[backspace]", class: "action hide-shift" },
    ],
    [
      "H",
      "O",
      "C",
      "N",
      "S",
      "P",
      "Cl",
      "Br",
      "I",
      "F",
    ],
    [
      "Na",
      "K",
      "Ca",
      "Mg",
      "Al",
      "Zn",
      "Fe",
      "Cu",
      "Ag",
      "Pb",
    ],
    [
      { latex: "(s)", insert: "(s)" },
      { latex: "(l)", insert: "(l)" },
      { latex: "(g)", insert: "(g)" },
      { latex: "(aq)", insert: "(aq)" },
      { latex: "_{#?}", class: "small" },
      { latex: "^{#?}", class: "small" },
      { latex: "^{#?}_{#?}", class: "small" },
      { latex: "\\pu{#?}", class: "small" },
      "[left]",
      "[right]",
    ],
    [
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "0",
    ],
  ],
}

const CHEMISTRY_REACTIONS_LAYOUT: VirtualKeyboardLayout = {
  id: "chemistry-reactions",
  label: "Rxn",
  labelClass: "MLK__tex-math",
  tooltip: "Reaction arrows and notation",
  rows: [
    [
      { latex: "->", insert: " -> " },
      { latex: "<=>", insert: " <=> " },
      { latex: "<->", insert: " <-> " },
      { latex: "<=", insert: " <= " },
      { latex: "=>", insert: " => " },
      { latex: "\\rightleftharpoons", insert: " <=> " },
      { latex: "[#?]", class: "small" },
      { latex: "->[#?]", class: "small" },
      { latex: "->[#?][#?]", class: "small" },
    ],
    [
      { latex: "+", insert: " + " },
      { latex: "=", insert: " = " },
      { latex: "v", insert: " v " },
      { latex: "^", insert: " ^ " },
      { latex: ".", insert: "." },
      { latex: "*", insert: " * " },
      { latex: "/", insert: "/" },
      { latex: "\\pm", insert: " \\pm " },
    ],
    [
      { latex: "(aq)", insert: "(aq)" },
      { latex: "(s)", insert: "(s)" },
      { latex: "(l)", insert: "(l)" },
      { latex: "(g)", insert: "(g)" },
      { latex: "[H+]", insert: "[H+]" },
      { latex: "[OH-]", insert: "[OH-]" },
      { latex: "\\Delta", insert: "[Delta]" },
      { latex: "[h\\nu]", insert: "[h\\nu]" },
    ],
    [
      { latex: "e^-", insert: "e^-" },
      { latex: "H^+", insert: "H^+" },
      { latex: "OH^-", insert: "OH^-" },
      { latex: "SO4^{2-}", insert: "SO4^{2-}" },
      { latex: "CO3^{2-}", insert: "CO3^{2-}" },
      { latex: "NH4^+", insert: "NH4^+" },
      "[left]",
      "[right]",
    ],
  ],
}

const CHEMISTRY_IONS_LAYOUT: VirtualKeyboardLayout = {
  id: "chemistry-ions",
  label: "Ion",
  labelClass: "MLK__tex-math",
  tooltip: "Common ions and charges",
  rows: [
    [
      { latex: "H^+", insert: "H^+" },
      { latex: "OH^-", insert: "OH^-" },
      { latex: "Na^+", insert: "Na+" },
      { latex: "K^+", insert: "K+" },
      { latex: "Ca^{2+}", insert: "Ca^2+" },
      { latex: "Mg^{2+}", insert: "Mg^2+" },
      { latex: "Al^{3+}", insert: "Al^3+" },
      { label: "[backspace]", class: "action hide-shift" },
    ],
    [
      { latex: "Fe^{2+}", insert: "Fe^2+" },
      { latex: "Fe^{3+}", insert: "Fe^3+" },
      { latex: "Cu^{2+}", insert: "Cu^2+" },
      { latex: "Ag^+", insert: "Ag+" },
      { latex: "Cl^-", insert: "Cl-" },
      { latex: "Br^-", insert: "Br-" },
      { latex: "I^-", insert: "I-" },
    ],
    [
      { latex: "SO4^{2-}", insert: "SO4^2-" },
      { latex: "CO3^{2-}", insert: "CO3^2-" },
      { latex: "NO3^-", insert: "NO3-" },
      { latex: "PO4^{3-}", insert: "PO4^3-" },
      { latex: "NH4^+", insert: "NH4+" },
      { latex: "CH3COO^-", insert: "CH3COO-" },
    ],
    ["[left]", "[right]"],
  ],
}

export const MATH_KEYBOARD_LAYOUTS = [
  MATH_CORE_LAYOUT,
  MATH_CALCULUS_LAYOUT,
  "default",
  "symbols",
  "greek",
  "numeric",
  "alphabetic",
] as const

export const PHYSICS_KEYBOARD_LAYOUTS = [
  PHYSICS_MECHANICS_LAYOUT,
  PHYSICS_FIELDS_LAYOUT,
  PHYSICS_UNITS_LAYOUT,
  MATH_CORE_LAYOUT,
  "numeric",
  "symbols",
  "greek",
] as const

export const CHEMISTRY_KEYBOARD_LAYOUTS = [
  CHEMISTRY_FORMULA_LAYOUT,
  CHEMISTRY_REACTIONS_LAYOUT,
  CHEMISTRY_IONS_LAYOUT,
  "numeric",
  "symbols",
  "greek",
] as const
